/**
 * ENEMY MISSILES — homing ordnance the player can shoot down.
 *
 * The boss was firing a "swarm of missiles" that flew straight and was trivial
 * to sidestep. Two problems with that: they were not actually seeking, and
 * there was nothing to DO about them except move.
 *
 * This system fixes both:
 *
 *   1. They HOME. Bounded turn rate, so they genuinely chase but can still be
 *      out-turned by a player who commits early. A missile that turns instantly
 *      is a hitscan weapon wearing a costume.
 *   2. They are SHOOTABLE. Each carries HP and its own hitbox, so player fire
 *      destroys them. That converts an incoming salvo from a dodging problem
 *      into a decision: break lock, or shoot it out of the sky.
 *
 * That second property is what makes them fun rather than annoying — an
 * unavoidable threat you cannot interact with is just a tax.
 *
 * READABILITY. Missiles are visually distinct from bullets: larger, oriented
 * along their velocity, and trailing a plume. The player must never mistake
 * one for a pellet, because the correct response to each is different.
 */

import * as THREE from 'three'

const MAX = 40

export const ENEMY_MISSILE = {
  /** Hit points. Two player rounds at tier 2, so shooting them down is real
   *  but not free. */
  hp: 12,
  speed: 17,
  /** Degrees/sec. Enough to chase, low enough that a hard break defeats it. */
  turnRate: 95,
  lifetime: 6.5,
  /** Seconds of straight boost before guidance engages — the escape window. */
  armDelay: 0.55,
  /** Collision radius against the player. */
  radius: 0.44,
  /** Radius at which player shots can destroy it. Generous on purpose. */
  interceptRadius: 0.9,
} as const

export class EnemyMissileSystem {
  readonly group = new THREE.Group()

  private readonly active = new Uint8Array(MAX)
  private readonly x = new Float32Array(MAX)
  private readonly y = new Float32Array(MAX)
  private readonly vx = new Float32Array(MAX)
  private readonly vy = new Float32Array(MAX)
  private readonly hp = new Float32Array(MAX)
  private readonly age = new Float32Array(MAX)

  private readonly body: THREE.InstancedMesh
  private readonly plume: THREE.InstancedMesh
  private readonly dummy = new THREE.Object3D()

  count = 0

  constructor() {
    this.group.name = 'EnemyMissiles'

    // Hostile ordnance wears the enemy band (#FFB020 core / #FF4A10 rim) so it
    // is unmistakably theirs, but at a size no bullet ever reaches.
    const bodyGeo = new THREE.CapsuleGeometry(0.13, 0.5, 3, 6)
    const bodyMat = new THREE.MeshBasicMaterial({
      color: '#ffcf7a',
      toneMapped: false,
      depthWrite: false,
    })
    this.body = new THREE.InstancedMesh(bodyGeo, bodyMat, MAX)
    this.body.frustumCulled = false
    this.body.count = 0
    this.body.renderOrder = 23
    this.group.add(this.body)

    const plumeGeo = new THREE.CircleGeometry(0.3, 8)
    const plumeMat = new THREE.MeshBasicMaterial({
      color: '#ff5a18',
      transparent: true,
      opacity: 0.8,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      toneMapped: false,
    })
    this.plume = new THREE.InstancedMesh(plumeGeo, plumeMat, MAX)
    this.plume.frustumCulled = false
    this.plume.count = 0
    this.plume.renderOrder = 22
    this.group.add(this.plume)
  }

  /** Launch one. `side` fans the initial boost so a salvo spreads before turning in. */
  spawn(x: number, y: number, side: number, index: number): void {
    for (let i = 0; i < MAX; i += 1) {
      if (this.active[i]) continue
      this.active[i] = 1
      this.x[i] = x
      this.y[i] = y
      // Boost outward and slightly up — the arc is what makes a launch read
      // as a launch rather than as a fast bullet.
      this.vx[i] = side * (7 + index * 1.6)
      this.vy[i] = -8
      this.hp[i] = ENEMY_MISSILE.hp
      this.age[i] = 0
      this.count += 1
      return
    }
  }

  update(dt: number, playerX: number, playerY: number): void {
    const cfg = ENEMY_MISSILE
    for (let i = 0; i < MAX; i += 1) {
      if (!this.active[i]) continue
      this.age[i] += dt
      if (this.age[i] > cfg.lifetime) {
        this.deactivate(i)
        continue
      }

      // Guidance engages only after the arming delay. That window is the
      // player's chance to break the intercept geometry by committing early.
      if (this.age[i] > cfg.armDelay) {
        const dx = playerX - this.x[i]
        const dy = playerY - this.y[i]
        const desired = Math.atan2(dy, dx)
        const current = Math.atan2(this.vy[i], this.vx[i])
        let diff = desired - current
        while (diff > Math.PI) diff -= Math.PI * 2
        while (diff < -Math.PI) diff += Math.PI * 2
        const maxTurn = (cfg.turnRate * Math.PI) / 180 * dt
        const a = current + Math.max(-maxTurn, Math.min(maxTurn, diff))
        this.vx[i] = Math.cos(a) * cfg.speed
        this.vy[i] = Math.sin(a) * cfg.speed
      }

      this.x[i] += this.vx[i] * dt
      this.y[i] += this.vy[i] * dt
      if (Math.abs(this.x[i]) > 24 || this.y[i] < -30 || this.y[i] > 34) {
        this.deactivate(i)
      }
    }
    this.sync()
  }

  /**
   * Player fire destroys missiles. Returns hit positions so the caller can
   * spark and score them — intercepting should feel like an accomplishment,
   * because it is one.
   */
  intercept(
    sx: number,
    sy: number,
    damage: number,
  ): { hit: boolean; destroyed: boolean; x: number; y: number } {
    const r = ENEMY_MISSILE.interceptRadius
    for (let i = 0; i < MAX; i += 1) {
      if (!this.active[i]) continue
      const dx = this.x[i] - sx
      const dy = this.y[i] - sy
      if (dx * dx + dy * dy > r * r) continue
      this.hp[i] -= damage
      const px = this.x[i]
      const py = this.y[i]
      if (this.hp[i] <= 0) {
        this.deactivate(i)
        return { hit: true, destroyed: true, x: px, y: py }
      }
      return { hit: true, destroyed: false, x: px, y: py }
    }
    return { hit: false, destroyed: false, x: 0, y: 0 }
  }

  /** Lethal contact with the player. */
  collidePlayer(px: number, py: number, playerRadius: number): boolean {
    const r = ENEMY_MISSILE.radius + playerRadius
    for (let i = 0; i < MAX; i += 1) {
      if (!this.active[i]) continue
      const dx = this.x[i] - px
      const dy = this.y[i] - py
      if (dx * dx + dy * dy > r * r) continue
      this.deactivate(i)
      return true
    }
    return false
  }

  /** Bombs clear missiles like any other ordnance. */
  clear(): number {
    let n = 0
    for (let i = 0; i < MAX; i += 1) {
      if (!this.active[i]) continue
      this.deactivate(i)
      n += 1
    }
    return n
  }

  private deactivate(i: number): void {
    this.active[i] = 0
    this.count = Math.max(0, this.count - 1)
  }

  private sync(): void {
    let n = 0
    for (let i = 0; i < MAX; i += 1) {
      if (!this.active[i]) continue
      const a = Math.atan2(this.vy[i], this.vx[i]) - Math.PI / 2
      this.dummy.position.set(this.x[i], this.y[i], 0.35)
      this.dummy.rotation.set(0, 0, a)
      this.dummy.scale.setScalar(1)
      this.dummy.updateMatrix()
      this.body.setMatrixAt(n, this.dummy.matrix)

      // Plume trails behind and pulses, so a live missile reads as burning.
      const pulse = 0.8 + Math.sin(this.age[i] * 34) * 0.2
      this.dummy.position.set(
        this.x[i] - Math.cos(a + Math.PI / 2) * 0.42,
        this.y[i] - Math.sin(a + Math.PI / 2) * 0.42,
        0.3,
      )
      this.dummy.scale.setScalar(pulse)
      this.dummy.updateMatrix()
      this.plume.setMatrixAt(n, this.dummy.matrix)
      n += 1
    }
    this.body.count = n
    this.plume.count = n
    this.body.instanceMatrix.needsUpdate = true
    this.plume.instanceMatrix.needsUpdate = true
  }

  reset(): void {
    this.active.fill(0)
    this.count = 0
    this.sync()
  }

  dispose(): void {
    this.body.geometry.dispose()
    this.plume.geometry.dispose()
    ;(this.body.material as THREE.Material).dispose()
    ;(this.plume.material as THREE.Material).dispose()
  }
}
